// ---------------------------------------------------------------------------
// YouAreYou (uru) — Auth Provider Component
//
// This file ONLY exports the AuthProvider React component.
// Types and context object live in @/context/authTypes.ts
// The useAuth hook lives in @/hooks/useAuth.ts
//
// This separation satisfies eslint-plugin-react-refresh which requires
// that files exporting components don't also export non-component bindings.
// ---------------------------------------------------------------------------

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { apiClient } from "@/api/client";
import {
  magicLogin,
  getCurrentUser,
  passkeyLoginBegin,
  passkeyLoginComplete,
  smsLogin,
  smsVerify,
} from "@/api/endpoints";
import type {
  AuthLoginData,
  UserProfile,
  PasskeyLoginBeginData,
} from "@/types/api";
import {
  startAuthentication,
  type AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

import { AuthContext } from "@/context/authTypes";
import type { AuthStatus, AuthContextValue } from "@/context/authTypes";

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  // If no tokens exist in memory, we can resolve "unauthenticated" immediately
  // without waiting for an async check.
  const [status, setStatus] = useState<AuthStatus>(() =>
    apiClient.hasTokens() ? "loading" : "unauthenticated",
  );
  const [user, setUser] = useState<UserProfile | null>(null);
  const [shouldPromptPasskey, setShouldPromptPasskey] = useState(false);

  // Guard against double-initialization in StrictMode
  const initializedRef = useRef(false);

  // Compute initial status synchronously — if no tokens in memory,
  // we know immediately that the user is unauthenticated.
  // This avoids calling setState inside an effect for the common case.
  const [initialStatusResolved] = useState(() => {
    if (!apiClient.hasTokens()) {
      return "unauthenticated" as const;
    }
    return null; // Need async check
  });

  // ---- Helpers ----

  /** After obtaining tokens, fetch the user profile and mark as authenticated. */
  const establishSession = useCallback(
    async (loginData: AuthLoginData, isFirstLogin: boolean = false) => {
      apiClient.setTokens(
        loginData.access_token,
        loginData.refresh_token,
        loginData.expires_in,
      );

      try {
        const profile = await getCurrentUser();
        setUser(profile);
        setStatus("authenticated");

        // Prompt passkey registration from the second login onwards.
        // Skip on the very first login (immediately after registration/KYC)
        // so we don't overwhelm new users before they've even seen the app.
        if (isFirstLogin) {
          const hasLoggedInBefore = localStorage.getItem("uru_has_logged_in") === "true";
          if (hasLoggedInBefore) {
            setShouldPromptPasskey(true);
          } else {
            localStorage.setItem("uru_has_logged_in", "true");
          }
        }
      } catch {
        // If we can't fetch the profile even with fresh tokens, bail out
        apiClient.clearTokens();
        setUser(null);
        setStatus("unauthenticated");
        throw new Error("Failed to fetch user profile after login.");
      }
    },
    [],
  );

  // ---- Login Methods ----

  const loginWithMagicLink = useCallback(
    async (token: string) => {
      const loginData = await magicLogin({ token });
      await establishSession(loginData, true);
    },
    [establishSession],
  );

  const loginWithPasskey = useCallback(
    async (phone: string) => {
      // Step 1: Get challenge from server
      const beginData: PasskeyLoginBeginData = await passkeyLoginBegin({
        username: phone,
      });

      // Step 2: Use SimpleWebAuthn to call navigator.credentials.get()
      let authResponse: AuthenticationResponseJSON;
      try {
        authResponse = await startAuthentication({
          optionsJSON: beginData.publicKey,
        });
      } catch (err) {
        // User cancelled or WebAuthn not available
        const message =
          err instanceof Error ? err.message : "Passkey authentication failed";
        throw new Error(message);
      }

      // Step 3: Send credential to server
      const loginData = await passkeyLoginComplete({
        challenge_id: beginData.challenge_id,
        credential: {
          id: authResponse.id,
          rawId: authResponse.rawId,
          type: authResponse.type,
          response: {
            clientDataJSON: authResponse.response.clientDataJSON,
            authenticatorData: authResponse.response.authenticatorData,
            signature: authResponse.response.signature,
            userHandle: authResponse.response.userHandle ?? null,
          },
        },
      });

      await establishSession(loginData, false);
    },
    [establishSession],
  );

  const sendSmsOtp = useCallback(async (phone: string) => {
    await smsLogin({ username: phone });
    // Always succeeds (enumeration prevention) — no state change needed
  }, []);

  const verifySmsOtp = useCallback(
    async (phone: string, code: string) => {
      const loginData = await smsVerify({ username: phone, code });
      // isFirstLogin: true — SMS OTP is now the primary first-login path (magic link deprecated).
      // Triggers the passkey registration prompt after successful OTP verification.
      await establishSession(loginData, true);
    },
    [establishSession],
  );

  // ---- Session Management ----

  const logout = useCallback(() => {
    apiClient.clearTokens();
    setUser(null);
    setStatus("unauthenticated");
    setShouldPromptPasskey(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!apiClient.hasTokens()) return;
    try {
      const profile = await getCurrentUser();
      setUser(profile);
    } catch {
      // If profile fetch fails, don't logout — might be a transient error.
      // The apiClient's 401 handler will force logout if needed.
    }
  }, []);

  // ---- Listen for forced logout (e.g., refresh token expired) ----

  useEffect(() => {
    const unsubscribe = apiClient.onAuthEvent("logout", () => {
      setUser(null);
      setStatus("unauthenticated");
      setShouldPromptPasskey(false);
    });
    return unsubscribe;
  }, []);

  // ---- Initial session check ----
  // On mount, check if we still have a valid session. Since tokens are
  // in-memory, they won't survive a full page reload. However, if this
  // context re-mounts (e.g., HMR or parent re-render), we check.
  //
  // The synchronous "no tokens" case is handled above via initialStatusResolved
  // to avoid calling setState within an effect body.

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // If we already resolved status synchronously, nothing to do
    if (initialStatusResolved === "unauthenticated") {
      return;
    }

    // We have tokens in memory — try to fetch the profile
    getCurrentUser()
      .then((profile) => {
        setUser(profile);
        setStatus("authenticated");
      })
      .catch(() => {
        apiClient.clearTokens();
        setStatus("unauthenticated");
      });
  }, [initialStatusResolved]);

  // ---- Context Value ----

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      shouldPromptPasskey,
      setShouldPromptPasskey,
      loginWithMagicLink,
      loginWithPasskey,
      sendSmsOtp,
      verifySmsOtp,
      logout,
      refreshProfile,
    }),
    [
      status,
      user,
      shouldPromptPasskey,
      loginWithMagicLink,
      loginWithPasskey,
      sendSmsOtp,
      verifySmsOtp,
      logout,
      refreshProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
